import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateOrderFormField } from '../../redux_files/actions/orderFormActions';
import { useMealAvailability } from '../../hooks/useMealAvailability';
import { PHONE_NUMBER, PHONE_NUMBER_WITH_PREFIX } from '../../utils/constants';
import { THEME } from '../../utils/theme';
import { LUNCH_CUTOFF_MINUTES_FOR_DISPLAY, DINNER_CUTOFF_MINUTES_FOR_DISPLAY, LUNCH_DELIVERY_TIME, DINNER_DELIVERY_TIME } from '../../utils/constants';

const MealTypeStep = () => {
  const dispatch = useDispatch();
  const { selectedDate, mealType, errors } = useSelector(state => state.orderForm);
  
  // Check meal availability based on current time using custom hook
  const mealAvailability = useMealAvailability(selectedDate);
  
  // Styles object
  const styles = {
    selectedDateDisplay: {
      marginBottom: '20px'
    },
    errorMessage: {
      marginBottom: '15px'
    },
    closedFullContainer: {
      marginTop: '15px',
      padding: '12px',
      backgroundColor: THEME.error.light,
      borderLeft: `4px solid ${THEME.error.main}`,
      borderRadius: '4px',
      color: THEME.error.darkText,
      fontSize: '13px',
      lineHeight: '1.6'
    },
    closedPartialContainer: {
      marginTop: '15px',
      padding: '12px',
      backgroundColor: THEME.warning.light,
      borderLeft: `4px solid ${THEME.warning.main}`,
      borderRadius: '4px',
      color: THEME.warning.dark,
      fontSize: '13px',
      lineHeight: '1.6'
    },
    closedPartialLink: {
      color: THEME.error.darkText,
      textDecoration: 'underline'
    },
    mealInfoDiv: {
      marginTop: '8px'
    },
    mealSubDiv: {
      marginTop: '5px'
    },
    mealSubDivWithMargin: (hasMargin) => ({
      marginTop: hasMargin ? '12px' : 0
    })
  };
  
  return (
    <>
      <div className='selected-date-display' style={styles.selectedDateDisplay}>
        Delivery Date: <strong>{new Date(selectedDate).toLocaleDateString('en-IN', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</strong>
      </div>
      
      <div className='options-grid'>
        {mealAvailability.lunchAvailable && (
          <div
            className={`option-card meal-card ${mealType === 'lunch' ? 'selected' : ''}`}
            onClick={() => dispatch(updateOrderFormField('mealType', 'lunch'))}
          >
            <div className='option-icon'>🍽️</div>
            <div className='option-label'>Lunch</div>
            <div className='availability-badge available'>
              ✓ Available
            </div>
            <div className='meal-timing'>
              <small>Order till {LUNCH_CUTOFF_MINUTES_FOR_DISPLAY}</small>
              <small>Delivery Time: {LUNCH_DELIVERY_TIME}</small>
            </div>
          </div>
        )}
        {mealAvailability.dinnerAvailable && (
          <div
            className={`option-card meal-card ${mealType === 'dinner' ? 'selected' : ''}`}
            onClick={() => dispatch(updateOrderFormField('mealType', 'dinner'))}
          >
            <div className='option-icon'>🌙</div>
            <div className='option-label'>Dinner</div>
            <div className='availability-badge available'>
              ✓ Available
            </div>
            <div className='meal-timing'>
              <small>Order till {DINNER_CUTOFF_MINUTES_FOR_DISPLAY}</small>
              <small>Delivery Time: {DINNER_DELIVERY_TIME}</small>
            </div>
          </div>
        )}
      </div>
      
      {errors.meal && (
        <div className='error-message' style={styles.errorMessage}>{errors.meal}</div>
      )}

      {/* Show closed message for today */}
      {(() => {
        const today = new Date();
        const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        const isToday = selectedDate === todayString;

        // Check if no meals are available
        if (isToday && !mealAvailability.lunchAvailable && !mealAvailability.dinnerAvailable) {
          return (
            <div style={styles.closedFullContainer}>
              <strong>🔒 New Orders are closed for Today</strong>
              <div style={styles.mealInfoDiv}>
                Lunch and Dinner orders for today are now closed.<br />
                Place your order tomorrow before {LUNCH_CUTOFF_MINUTES_FOR_DISPLAY} to enjoy fresh and timely service!<br />
                For special order please call us at <a href={`tel:${PHONE_NUMBER_WITH_PREFIX}`} style={styles.closedPartialLink}>{PHONE_NUMBER}</a>
              </div>
            </div>
          );
        }

        // Show specific closure messages for individual meals
        if (isToday && (!mealAvailability.lunchAvailable || !mealAvailability.dinnerAvailable)) {
          return (
            <div style={styles.closedPartialContainer}>
              {!mealAvailability.lunchAvailable && (
                <div>
                  <strong>🍽️ Lunch orders closed for today.</strong>
                  <div style={styles.mealSubDiv}>Please order tomorrow before {LUNCH_CUTOFF_MINUTES_FOR_DISPLAY}</div>
                </div>
              )}
              {!mealAvailability.dinnerAvailable && (
                <div style={styles.mealSubDivWithMargin(!mealAvailability.lunchAvailable)}>
                  <strong>🌙 Dinner orders closed for today.</strong>
                  <div style={styles.mealSubDiv}>Please order tomorrow before {DINNER_CUTOFF_MINUTES_FOR_DISPLAY}</div>
                </div>
              )}
            </div>
          );
        }
        return null;
      })()}
    </>
  );
};

export default MealTypeStep;
